import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { MOCK_CARD_ICONS } from '../../lib/mockData';

// Categories are loaded live from Supabase so newly created categories
// in CategoryLibrary appear here automatically — no hardcoded list needed.

function CategoryLabel(str) {
  return str.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' ');
}

export default function IconLibrary() {
  const [icons, setIcons] = useState([]);
  const [categories, setCategories] = useState([]); // live from DB
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingIcon, setEditingIcon] = useState(null);
  const [form, setForm] = useState({ name: '', category: [], active: true });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null); // { type: 'success'|'error', message: string }
  const fileRef = useRef();
  const formTopRef = useRef();

  function showToast(type, message) {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  }

  useEffect(() => {
    fetchIcons();
    fetchCategories();
  }, []);

  async function fetchCategories() {
    if (isSupabaseConfigured()) {
      const { data } = await supabase
        .from('design_categories_1739812345679')
        .select('id, label')
        .eq('active', true)
        .order('sort_order');
      setCategories(data || []);
    }
  }

  async function fetchIcons() {
    setLoading(true);
    if (isSupabaseConfigured()) {
      const { data } = await supabase.from('card_icons_1739812345678').select('*').order('sort_order');
      setIcons(data || MOCK_CARD_ICONS);
    } else {
      setIcons(MOCK_CARD_ICONS);
    }
    setLoading(false);
  }

  function openNew() {
    setError('');
    setEditingIcon(null);
    setForm({ name: '', category: [], active: true });
    setImageFile(null);
    setImagePreview('');
    setShowForm(true);
    setTimeout(() => formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function openEdit(icon) {
    setError('');
    setEditingIcon(icon);
    setForm({ name: icon.name, category: icon.category || [], active: icon.active });
    setImagePreview(icon.image_url);
    setImageFile(null);
    setShowForm(true);
    setTimeout(() => formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  function toggleCategory(cat) {
    setForm(f => ({
      ...f,
      category: f.category.includes(cat) ? f.category.filter(c => c !== cat) : [...f.category, cat]
    }));
  }

  async function handleSave() {
    setError('');
    if (!imageFile && !editingIcon?.image_url) {
      setError('Please upload a card image.');
      return;
    }

    setSaving(true);
    try {
      if (isSupabaseConfigured()) {
        let imageUrl = editingIcon?.image_url || '';
        if (imageFile) {
          const fileName = `icons/${Date.now()}-${imageFile.name}`;
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('design-images')
            .upload(fileName, imageFile, { upsert: true });

          if (uploadError) throw uploadError;
          const { data: urlData } = supabase.storage.from('design-images').getPublicUrl(fileName);
          imageUrl = urlData.publicUrl;
        }

        const payload = { name: form.name, category: form.category, active: form.active, image_url: imageUrl };
        if (editingIcon) {
          const { error: updateError } = await supabase.from('card_icons_1739812345678').update(payload).eq('id', editingIcon.id);
          if (updateError) throw updateError;
        } else {
          const { error: insertError } = await supabase.from('card_icons_1739812345678').insert({ ...payload, sort_order: icons.length });
          if (insertError) throw insertError;
        }
        await fetchIcons();
        setShowForm(false);
        showToast('success', editingIcon ? `"${form.name}" updated successfully.` : `"${form.name}" added to library.`);
      } else {
        if (editingIcon) {
          setIcons(d => d.map(x => x.id === editingIcon.id ? { ...x, ...form, image_url: imagePreview } : x));
        } else {
          setIcons(d => [...d, { id: `ci${Date.now()}`, ...form, image_url: imagePreview || 'https://cdn-icons-png.flaticon.com/512/833/833472.png', sort_order: d.length }]);
        }
        setShowForm(false);
        showToast('success', editingIcon ? `"${form.name}" updated.` : `"${form.name}" added.`);
      }
    } catch (err) {
      setError(err.message || 'Save failed.');
      showToast('error', err.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate(icon) {
    const newStatus = !icon.active;
    if (isSupabaseConfigured()) {
      const { error } = await supabase.from('card_icons_1739812345678').update({ active: newStatus }).eq('id', icon.id);
      if (error) {
        showToast('error', 'Failed to update status.');
        return;
      }
    }
    setIcons(d => d.map(x => x.id === icon.id ? { ...x, active: newStatus } : x));
    showToast('success', `"${icon.name}" ${newStatus ? 'activated' : 'deactivated'}.`);
  }

  return (
    <div className="space-y-6">

      {/* ── Toast notification ── */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-semibold border ${
              toast.type === 'success'
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`}
          >
            <SafeIcon name={toast.type === 'success' ? 'CheckCircle' : 'AlertCircle'} className="text-lg shrink-0" />
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-serif text-charcoal">Card Image Library</h2>
          <p className="text-sage-500 text-sm">Imagery for card personalization (PNGs recommended).</p>
        </div>
        <button onClick={openNew} className="bg-blush-300 hover:bg-blush-400 text-white px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors shadow-soft">
          <SafeIcon name="Plus" /> New Card Image
        </button>
      </div>

      <div ref={formTopRef} />
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="bg-white border border-sage-100 rounded-2xl p-6 shadow-soft space-y-6">
            <h3 className="font-serif text-xl text-charcoal">{editingIcon ? 'Edit Card Image' : 'New Card Image'}</h3>

            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100 text-sm flex items-center gap-2">
                <SafeIcon name="AlertCircle" /> {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">Image Name *</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="e.g. Rose Heart, Graduation Cap"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-1">
                    Occasions (Categories)
                    <span className="ml-2 text-[10px] font-normal text-sage-400">
                      — controls which vase occasion shows this sticker
                    </span>
                  </label>
                  <p className="text-[11px] text-sage-400 mb-2 leading-relaxed">
                    Select <strong className="text-sage-600">General</strong> to show this sticker for all occasions. Select specific occasions to only show it when that vase category is chosen.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {/* "General" is always shown first as a catch-all option */}
                    {[{ id: 'general', label: 'General' }, ...categories.filter(c => c.id !== 'general')].map(cat => (
                      <label
                        key={cat.id}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer border transition-colors ${
                          form.category.includes(cat.id)
                            ? 'bg-blush-300 text-white border-blush-300'
                            : 'bg-white border-sage-200 text-sage-600 hover:border-blush-200'
                        }`}
                      >
                        <input type="checkbox" className="sr-only" checked={form.category.includes(cat.id)} onChange={() => toggleCategory(cat.id)} />
                        {cat.label}
                      </label>
                    ))}
                  </div>
                  {form.category.length === 0 && (
                    <p className="text-amber-500 text-[11px] font-bold mt-2 flex items-center gap-1">
                      <SafeIcon name="AlertTriangle" className="text-xs" />
                      No occasion selected — this sticker won't appear on any order form until you tag it.
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2">Upload Image *</label>
                <div
                  onClick={() => fileRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl aspect-square flex flex-col items-center justify-center cursor-pointer transition-colors relative overflow-hidden bg-sage-50 ${
                    error && !imagePreview ? 'border-red-300' : 'border-sage-200 hover:border-blush-300'
                  }`}
                >
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="absolute inset-0 w-full h-full object-contain p-8" />
                  ) : (
                    <>
                      <SafeIcon name="Upload" className="text-3xl text-sage-400 mb-2" />
                      <p className="text-sm text-sage-400 text-center px-4">Click to upload image</p>
                      <p className="text-[11px] text-sage-300 mt-1">PNG with transparent background works best</p>
                    </>
                  )}
                </div>
                {imagePreview && (
                  <button
                    onClick={() => { setImagePreview(''); setImageFile(null); }}
                    className="mt-2 text-xs text-sage-400 hover:text-red-400 transition-colors flex items-center gap-1"
                  >
                    <SafeIcon name="X" className="text-xs" /> Remove
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*" className="sr-only" onChange={handleFileChange} />
              </div>
            </div>

            <div className="flex gap-3 pt-2 border-t border-sage-50">
              <button
                onClick={handleSave}
                disabled={saving || !form.name}
                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <SafeIcon name="Save" />
                    {editingIcon ? 'Save Changes' : 'Add Card Image'}
                  </>
                )}
              </button>
              <button onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancel</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blush-300"></div></div>
      ) : icons.length === 0 ? (
        <div className="py-16 text-center text-sage-400 border-2 border-dashed border-sage-100 rounded-2xl">
          <SafeIcon name="Image" className="text-3xl mx-auto mb-3 text-sage-200" />
          <p className="font-bold text-sm">No card images yet</p>
          <p className="text-xs mt-1">Click "New Card Image" to add your first sticker.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {icons.map(icon => {
            const taggedCats = Array.isArray(icon.category)
              ? icon.category
              : typeof icon.category === 'string'
                ? icon.category.split(',').map(s => s.trim()).filter(Boolean)
                : [];
            return (
              <div key={icon.id} className={`relative rounded-2xl overflow-hidden border transition-all ${icon.active ? 'border-sage-100' : 'border-sage-100 opacity-50'}`}>
                <div className="aspect-square bg-warmwhite flex items-center justify-center p-6">
                  <img src={icon.image_url} alt={icon.name} className="w-full h-full object-contain" />
                </div>
                <div className="p-3 bg-white border-t border-sage-50 space-y-2">
                  <p className="text-xs font-bold text-charcoal truncate">{icon.name}</p>

                  {/* Category tags — shows exactly what's saved */}
                  {taggedCats.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {taggedCats.slice(0, 3).map(cat => (
                        <span key={cat} className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-blush-50 text-blush-400 border border-blush-100">
                          {cat}
                        </span>
                      ))}
                      {taggedCats.length > 3 && (
                        <span className="text-[8px] text-sage-400">+{taggedCats.length - 3}</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-500 border border-amber-100">
                      ⚠ untagged
                    </span>
                  )}

                  <div className="flex gap-1">
                    <button onClick={() => openEdit(icon)} className="flex-1 text-[10px] bg-sage-50 hover:bg-sage-100 text-sage-600 py-1 rounded-lg transition-colors">Edit</button>
                    <button onClick={() => handleDeactivate(icon)} className={`flex-1 text-[10px] py-1 rounded-lg transition-colors ${icon.active ? 'bg-red-50 text-red-500' : 'bg-green-50 text-green-600'}`}>
                      {icon.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
